export type AuthActivationType = 'invite' | 'recovery';

export interface AuthActivationRoute {
  path: '/auth/accept' | '/auth/recovery';
  expectedType: AuthActivationType;
  tokenHash: string | null;
}

interface LocationLike {
  pathname: string;
  search: string;
  hash: string;
}

interface HistoryLike {
  state: unknown;
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

const ACTIVATION_PATHS = new Map<string, AuthActivationType>([
  ['/auth/accept', 'invite'],
  ['/auth/recovery', 'recovery'],
]);

function validTokenHash(value: string | null): value is string {
  return Boolean(
    value &&
      value.length <= 2048 &&
      ![...value].some((character) => {
        const codePoint = character.codePointAt(0) ?? 0;
        return /\s/u.test(character) || codePoint < 32 || codePoint === 127;
      })
  );
}

export function consumeAuthActivationRoute(
  location: LocationLike,
  history: HistoryLike
): AuthActivationRoute | null {
  const expectedType = ACTIVATION_PATHS.get(location.pathname);
  if (!expectedType) return null;

  const search = location.search;
  const hash = location.hash;
  history.replaceState(history.state, '', location.pathname);

  const params = new URLSearchParams(search);
  const keys = [...params.keys()];
  const hasOnlyExpectedParameters =
    keys.length === 2 &&
    params.getAll('token_hash').length === 1 &&
    params.getAll('type').length === 1;
  const tokenHash = params.get('token_hash');
  const type = params.get('type');

  return {
    path: location.pathname as AuthActivationRoute['path'],
    expectedType,
    tokenHash:
      !hash && hasOnlyExpectedParameters && type === expectedType && validTokenHash(tokenHash)
        ? tokenHash
        : null,
  };
}

export function recoveryRedirectUrl(origin: string): string {
  return new URL('/auth/recovery', origin).toString();
}

export function passwordPolicyError(password: string): string | null {
  if (password.length < 12) return 'Use at least 12 characters.';
  if (!/[a-z]/u.test(password)) return 'Add a lowercase letter.';
  if (!/[A-Z]/u.test(password)) return 'Add an uppercase letter.';
  if (!/[0-9]/u.test(password)) return 'Add a number.';
  if (!/[^A-Za-z0-9]/u.test(password)) return 'Add a symbol.';
  return null;
}
